import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import {
  Search, X, Music, Waves, Sparkles, LifeBuoy, NotebookPen, Globe,
  Loader2, ArrowUpRight, Link as LinkIcon, Target,
} from "lucide-react";

const ICONS = {
  frequency: Music, binaural: Waves, tool: Sparkles,
  resource: LifeBuoy, journal: NotebookPen, goal: Target,
};

const UniversalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [internalTotal, setInternalTotal] = useState(0);
  const [web, setWeb] = useState(null); // {configured, items}
  const [loading, setLoading] = useState(false);
  const [webLoading, setWebLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
    else { setQ(""); setGroups([]); setWeb(null); setSearched(false); setInternalTotal(0); }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const runWeb = useCallback(async (query) => {
    setWebLoading(true);
    try {
      const { data } = await api.get(`/search/web`, { params: { q: query, safe: true } });
      setWeb(data);
    } catch {
      setWeb({ configured: true, items: [], error: true });
    } finally {
      setWebLoading(false);
    }
  }, []);

  useEffect(() => {
    const query = q.trim();
    setWeb(null);
    if (query.length < 2) { setGroups([]); setInternalTotal(0); setSearched(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/internal`, { params: { q: query } });
        setGroups(data.groups || []);
        setInternalTotal(data.total || 0);
        setSearched(true);
        if ((data.total || 0) === 0) runWeb(query);
      } catch {
        setGroups([]); setInternalTotal(0); setSearched(true);
      } finally {
        setLoading(false);
      }
    }, 320);
    return () => clearTimeout(t);
  }, [q, runWeb]);

  const goInternal = (item) => {
    onClose();
    if (!user) {
      // Guests can reach sounds; anything account-bound routes to registration.
      if (["tool", "frequency", "binaural"].includes(item.type)) navigate("/sounds");
      else navigate("/register");
      return;
    }
    navigate(item.route);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[8vh] pb-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="universal-search-overlay"
        >
          <div className="absolute inset-0 bg-[#070d11]/80 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 24, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="relative w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0f1a20]/85 backdrop-blur-2xl shadow-2xl overflow-hidden"
            style={{ boxShadow: "0 24px 80px -20px rgba(0,0,0,0.7)" }}
            data-testid="universal-search-panel"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search className="w-5 h-5 text-teal-200/70 shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search frequencies, tones, your mind dumps, tools…"
                className="flex-1 bg-transparent text-white placeholder-white/35 text-[15px] outline-none"
                data-testid="universal-search-input"
              />
              {loading && <Loader2 className="w-4 h-4 text-white/40 animate-spin" />}
              <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors" data-testid="universal-search-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[62vh] overflow-y-auto px-3 py-3">
              {q.trim().length < 2 && (
                <p className="text-center text-white/35 text-sm py-10 px-6" data-testid="search-hint">
                  Type to explore Aurea — Solfeggio & binaural tones, your past Mind Dump logs, depth tools and
                  support resources. If nothing matches inside, we'll gently open the wider web for you.
                </p>
              )}

              {groups.map((g) => (
                <div key={g.label} className="mb-4" data-testid={`search-group-${g.label}`}>
                  <p className="text-[11px] uppercase tracking-widest text-teal-200/50 px-3 mb-1.5">{g.label}</p>
                  <div className="space-y-1.5">
                    {g.items.map((item, i) => {
                      const Icon = ICONS[item.type] || Sparkles;
                      return (
                        <button
                          key={i}
                          onClick={() => goInternal(item)}
                          className="w-full text-left flex items-start gap-3 rounded-2xl px-3 py-3 bg-white/5 hover:bg-white/[0.09] border border-white/5 hover:border-white/15 transition-all group"
                          data-testid="search-internal-result"
                        >
                          <span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-teal-200/80" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-white text-sm font-medium truncate">{item.title}</span>
                            <span className="block text-white/45 text-xs leading-snug line-clamp-2">{item.subtitle}</span>
                          </span>
                          {item.meta && <span className="text-[10px] text-white/30 shrink-0 mt-1">{item.meta}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Web bridge */}
              {searched && internalTotal === 0 && (
                <div className="mt-1" data-testid="web-bridge">
                  <div className="flex items-center gap-2 px-3 mb-2">
                    <Globe className="w-4 h-4 text-teal-200/60" />
                    <p className="text-[11px] uppercase tracking-widest text-teal-200/50">Searching the wider web</p>
                    {webLoading && <Loader2 className="w-3.5 h-3.5 text-white/40 animate-spin ml-1" />}
                  </div>

                  {web && web.configured === false && (
                    <div className="rounded-2xl px-4 py-5 bg-white/5 border border-white/10 text-center" data-testid="web-not-configured">
                      <p className="text-white/70 text-sm">Web search isn't connected yet.</p>
                      <p className="text-white/40 text-xs mt-1.5 leading-relaxed">
                        Nothing matched inside Aurea for "{q.trim()}". Once web search is enabled, results from
                        across the web will appear here — calmly, right inside your sanctuary.
                      </p>
                    </div>
                  )}

                  {web && web.error && (
                    <div className="rounded-2xl px-4 py-5 bg-white/5 border border-white/10 text-center">
                      <p className="text-white/60 text-sm">The web is quiet right now. Please try again shortly.</p>
                    </div>
                  )}

                  {web && web.configured && !web.error && web.items?.length === 0 && !webLoading && (
                    <p className="text-center text-white/40 text-sm py-6">No web results for "{q.trim()}".</p>
                  )}

                  <div className="space-y-1.5">
                    {web?.items?.map((it, i) => (
                      <a
                        key={i}
                        href={it.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left flex items-start gap-3 rounded-2xl px-3 py-3 bg-white/5 hover:bg-white/[0.09] border border-white/5 hover:border-white/15 transition-all"
                        data-testid="search-web-result"
                      >
                        <span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                          <LinkIcon className="w-4 h-4 text-teal-200/80" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1 text-white text-sm font-medium">
                            <span className="truncate">{it.title}</span>
                            <ArrowUpRight className="w-3.5 h-3.5 text-white/40 shrink-0" />
                          </span>
                          <span className="block text-teal-200/40 text-[11px] truncate">{it.display}</span>
                          <span className="block text-white/45 text-xs leading-snug line-clamp-2 mt-0.5">{it.snippet}</span>
                        </span>
                      </a>
                    ))}
                  </div>
                  {web?.items?.length > 0 && (
                    <p className="text-center text-[10px] text-white/25 mt-3">SafeSearch on · results open in a new tab</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UniversalSearch;
